import { useMemo, useState, useCallback } from 'react';
import { DataGrid, KeenIcon } from '@/components';
import {
  useGetAllExternalServicesQuery,
  useUpdateExternalServiceMutation,
  useLazyGetExternalServiceErrorQuery,
} from '@/services/ExternalAPIServices';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { Statuses } from '@/constant';
import { selectHealthChecks } from '@/store/healthCheckSlice';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
} from '@/components/modal';
import { DefaultTooltip } from '@/components/tooltip/Tooltip';
import { formatDisplayDate, formatDisplayDateTime } from '@/utils/Date';

interface ThirdPartyError {
  id: string;
  name: string;
  message: string;
  error_message: string;
  created_at: string;
  last_successful_update_at?: string | null;
  reason?: string | null;
  reconnect_exhausted?: boolean;
}

interface ExternalService {
  id: string;
  name: string;
  type_id: number;
  status: Statuses;
  created_at: string;
  updated_at: string;
  inactive_duration?: number;
  is_healthy?: boolean;
  third_party_error?: ThirdPartyError[];
  health_check_id?: string;
  last_checked_at?: string | null;
}

interface StatusToggleProps {
  typeId: number;
  status: Statuses;
  isUpdating: boolean;
}

const StatusToggle = ({
  typeId,
  status: initialStatus,
  isUpdating,
}: StatusToggleProps) => {
  const [updateExternalService] = useUpdateExternalServiceMutation();
  const [currentStatus, setCurrentStatus] = useState(initialStatus);

  const handleToggle = async () => {
    const newStatus =
      currentStatus === Statuses.ACTIVE ? Statuses.INACTIVE : Statuses.ACTIVE;
    try {
      const response = await updateExternalService({
        type_id: typeId,
        status: newStatus,
      }).unwrap();
      if (response) {
        setCurrentStatus(response.status ? Statuses.ACTIVE : Statuses.INACTIVE);
      }
    } catch (error) {
      console.error('Failed to update service status:', error);
    }
  };

  return (
    <label
      className="relative inline-flex items-center cursor-pointer"
      aria-label={`Toggle status for service ${typeId}`}
    >
      <input
        type="checkbox"
        checked={currentStatus === Statuses.ACTIVE}
        onChange={handleToggle}
        disabled={isUpdating}
        className="sr-only peer cursor-pointer"
        aria-label={`Toggle status for service ${typeId}`}
      />
      <div
        className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full  transition-colors duration-300 ease-in-out ${
          currentStatus === Statuses.ACTIVE ? 'bg-green-600' : 'bg-red-600'
        } after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform after:duration-300 after:ease-in-out peer-checked:after:translate-x-full`}
      ></div>
    </label>
  );
};

const ExternalServices = () => {
  const { data, isLoading, isFetching, isError } =
    useGetAllExternalServicesQuery();
  const healthChecks = useSelector((state: RootState) =>
    selectHealthChecks(state)
  );
  const [, { isLoading: isUpdating }] = useUpdateExternalServiceMutation();
  const [getServiceError, { data: errorData, isLoading: isLoadingError }] =
    useLazyGetExternalServiceErrorQuery();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [selectedServiceName, setSelectedServiceName] = useState('');
  const [selectedServiceLastCheckedAt, setSelectedServiceLastCheckedAt] =
    useState<string | null | undefined>(undefined);

  const enrichedData = useMemo(() => {
    if (!data?.data) return [];

    return data.data.map((service: ExternalService) => {
      const healthCheck = healthChecks.find(
        (check) => check.type_id === service.type_id
      );
      return {
        ...service,
        inactive_duration: healthCheck?.inactive_duration || 0,
        is_healthy: healthCheck?.is_healthy ?? true,
        third_party_error: healthCheck?.third_party_error || [],
        health_check_id: healthCheck?.id,
        last_checked_at: healthCheck?.last_checked_at,
      };
    });
  }, [data, healthChecks]);

  const handleViewError = useCallback(
    async (
      errorId: string,
      serviceName: string,
      lastCheckedAt?: string | null
    ) => {
      setSelectedServiceName(serviceName);
      setSelectedServiceLastCheckedAt(lastCheckedAt);
      setIsErrorModalOpen(true);
      await getServiceError(errorId);
    },
    [getServiceError]
  );

  const handleCloseModal = useCallback(() => {
    setIsErrorModalOpen(false);
    setSelectedServiceName('');
    setSelectedServiceLastCheckedAt(undefined);
  }, []);

  // Normalize API error response (could be a single error or array on the service)
  const normalizedErrors = useMemo<ThirdPartyError[]>(() => {
    if (!errorData) return [];
    type ErrorLike =
      | { third_party_error?: ThirdPartyError[] }
      | ThirdPartyError;
    const response = errorData as ErrorLike;
    if (
      (response as { third_party_error?: ThirdPartyError[] }).third_party_error
    ) {
      return (
        (response as { third_party_error?: ThirdPartyError[] })
          .third_party_error || []
      );
    }
    return [response as ThirdPartyError];
  }, [errorData]);

  const columns = useMemo(
    () => [
      {
        accessor: (row: ExternalService) => row.name,
        id: 'name',
        header: 'Service Name',
        enableSorting: false,
        cell: ({ row }: { row: { original: ExternalService } }) => (
          <div className="relative flex items-center">
            <span className="text-sm font-medium text-gray-900">
              {row.original.name}
            </span>
            {(row.original.inactive_duration ?? 0) > 5 && (
              <>
                <DefaultTooltip
                  title={`Service inactive for ${row.original.inactive_duration} minutes`}
                  placement="top"
                  arrow
                >
                  <span
                    data-testid="inactive-service-indicator"
                    className="ml-2 inline-flex h-2 w-2 flex-shrink-0 rounded-full bg-red-500 animate-pulse"
                    aria-hidden="true"
                  />
                </DefaultTooltip>
                <span className="sr-only">
                  {`Service inactive for ${row.original.inactive_duration} minutes`}
                </span>
              </>
            )}
          </div>
        ),
        meta: { className: 'min-w-[200px]' },
      },

      {
        accessor: (row: ExternalService) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: ExternalService } }) => (
          <StatusToggle
            typeId={row.original.type_id}
            status={row.original.status}
            isUpdating={isUpdating}
          />
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: ExternalService) => row.type_id,
        id: 'type_id',
        header: 'Type ID',
        enableSorting: false,
        cell: ({ row }: { row: { original: ExternalService } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.type_id}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },

      {
        accessor: (row: ExternalService) => row.updated_at,
        id: 'updated_at',
        header: 'Updated At',
        enableSorting: false,
        cell: ({ row }: { row: { original: ExternalService } }) => (
          <span className="text-gray-800 font-normal">
            {formatDisplayDate(new Date(row.original.updated_at))}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: () => 'actions',
        id: 'actions',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: ExternalService } }) => (
          <div className="flex items-center gap-2">
            {(() => {
              if (row.original.is_healthy !== false) {
                return (
                  <span className="items-center gap-1 text-green-700 bg-green-50 border border-green-200 rounded px-1 py-1 text-xs">
                    <KeenIcon
                      icon="tick-circle"
                      className="text-green-600 text-sm"
                    />
                    Ok
                  </span>
                );
              }
              const errors = row.original.third_party_error || [];
              if (errors.length === 0) return null;
              const firstError = errors[0];
              if (!firstError) return null;
              return (
                <button
                  onClick={() =>
                    handleViewError(
                      firstError.id,
                      row.original.name,
                      row.original.last_checked_at
                    )
                  }
                  className="btn btn-sm btn-light btn-outline"
                  title="View Error Details"
                  aria-label={`View error details for ${row.original.name}`}
                >
                  <KeenIcon icon="information-2" className="text-lg" />
                  <span className="ml-1">View Error</span>
                </button>
              );
            })()}
          </div>
        ),
        meta: { className: 'min-w-[150px]' },
      },
    ],
    [isUpdating, handleViewError]
  );

  return (
    <>
      <div className="card card-grid min-w-full">
        <div className="card-body">
          {isLoading ? (
            <div className="text-center py-4" role="status" aria-live="polite">
              <KeenIcon
                type="filled"
                icon="spinner"
                className="text-gray-500 text-2xl animate-spin"
              />
              <span className="ml-2 text-gray-700">Loading...</span>
            </div>
          ) : isError ? (
            <div className="text-center py-4 text-red-600" role="alert">
              Error loading data. Please try again.
            </div>
          ) : (
            <>
              {!isLoading && !isError && enrichedData.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-gray-500">No Data available</p>
                </div>
              )}
              {!isLoading && !isError && enrichedData.length > 0 && (
                <DataGrid
                  data-testid="external-services-data-grid"
                  columns={columns}
                  data={enrichedData}
                  rowSelect={false}
                  loading={isLoading || isFetching || isUpdating}
                  pagination={{
                    page: 0,
                    size: 10,
                    totalItems: data?.total_items || 0,
                    totalPages: data?.total_pages || 1,
                    setPagination: () => {},
                    info: `{from} - {to} of {count}`,
                    sizes: [10, 25, 50, 100],
                    sizesLabel: 'Show',
                    sizesDescription: 'per page',
                  }}
                  sorting={[{ id: 'name', desc: false }]}
                  serverSide={false}
                  hasActiveFilters={false}
                  aria-label="External services table"
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Error Details Modal */}
      <Modal open={isErrorModalOpen} onClose={handleCloseModal}>
        <ModalContent className="max-w-3xl min-w-[520px] min-h-[220px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 fixed">
          <ModalHeader className="flex justify-between items-center border-b border-gray-200 p-5">
            <h1 className="text-lg font-semibold text-gray-900">
              Error Details - {selectedServiceName}
            </h1>
            <button
              onClick={handleCloseModal}
              className="btn btn-sm btn-icon btn-light"
              aria-label="Close modal"
            >
              <KeenIcon icon="cross" className="text-gray-600" />
            </button>
          </ModalHeader>
          <ModalBody className="p-5">
            {selectedServiceLastCheckedAt && (
              <p className="text-sm text-gray-600 mb-4">
                Last Checked: {formatDisplayDateTime(selectedServiceLastCheckedAt)}
              </p>
            )}
            {isLoadingError ? (
              <div className="text-center py-8">
                <KeenIcon
                  type="filled"
                  icon="spinner"
                  className="text-gray-500 text-2xl animate-spin"
                />
                <p className="mt-2 text-gray-600">Loading error details...</p>
              </div>
            ) : normalizedErrors.length > 0 ? (
              <div className="space-y-4 max-h-[500px] overflow-y-auto">
                {normalizedErrors.map((error: ThirdPartyError) => (
                  <div
                    key={error.id}
                    className="bg-red-50 border border-red-200 rounded-lg p-4"
                  >
                    <div className="flex items-start gap-3">
                      <KeenIcon
                        icon="information-2"
                        className="text-red-600 text-xl mt-0.5 flex-shrink-0"
                      />
                      <div className="flex-1">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <h2 className="font-semibold text-red-900">
                            {error.name}
                          </h2>
                          {error.reconnect_exhausted && (
                            <span className="inline-flex items-center gap-1 text-red-700 bg-red-100 border border-red-300 rounded px-1 py-1 text-xs flex-shrink-0">
                              <KeenIcon
                                icon="information-2"
                                className="text-red-600 text-sm"
                              />
                              Reconnect Exhausted
                            </span>
                          )}
                        </div>
                        <p className="text-red-800 whitespace-pre-wrap break-words mb-3">
                          {error.error_message
                            ? error.error_message.replace(/_/g, ' ')
                            : ''}
                        </p>
                        <div className="space-y-1 text-xs text-red-700">
                          <p>
                            Detected At
                            : {formatDisplayDateTime(error.created_at)}
                          </p>
                          {error.reason && (
                            <p>
                              Reason
                              : {error.reason}
                            </p>
                          )}
                          {error.last_successful_update_at && (
                            <p>
                              Last Successful Update
                              :{' '}
                              {formatDisplayDateTime(
                                error.last_successful_update_at
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No error details available
              </div>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ExternalServices;
